import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import { Menu } from '@headlessui/react';
import {
    XMarkIcon,
    CheckIcon,
    ArrowPathIcon,
    EyeIcon,
    EyeSlashIcon,
    DocumentDuplicateIcon,
    ChevronLeftIcon,
    ChevronRightIcon
} from '@heroicons/react/24/outline';
import sessionCheck from "../session/userData";
import { encryptDataHybrid } from "../enc/useEncrypt";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getDeviceInfo } from '../utils/deviceInfo';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_API_URL;

const RechargeAPIConfiguration = () => {
    const [searchParams] = useSearchParams();
    const providerId = searchParams.get('providerId');
    const companyName = searchParams.get('companyName');
    const [access_data, setAccess_data] = useState([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [activeConfigTab, setActiveConfigTab] = useState('recharge');
    const [tableName, setTableName] = useState('');
    const [currentTabData, setCurrentTabData] = useState({ attributes: [], loading: false });

    useEffect(() => {
        if (sessionCheck() == null) {
            navigate("../login", { replace: true });
        } else {
            setAccess_data(JSON.parse(sessionCheck()));
        }
    }, [navigate]);

    useEffect(() => {
        if (access_data?.username && providerId) {
            handleTabChange('recharge');
        }
    }, [access_data?.username, providerId]);

    const handleTabChange = async (tab) => {
        setActiveConfigTab(tab);
        setCurrentTabData({ attributes: [], loading: true });

        try {
            const response = await fetchTabConfig(tab);
            setCurrentTabData({
                attributes: transformConfigData(response.data, tab),
                loading: false
            });
            setTableName(response.table_name || 'provider_setting');
        } catch (error) {
            setCurrentTabData({ attributes: [], loading: false });
            toast.error(`Failed to load ${tab} configuration`);
        }
    };

    const fetchTabConfig = async (tab) => {
        const dataToEncrypt = {
            user_id: access_data?.username || '',
            login_id: access_data?.login_id || '',
            device_id: getDeviceInfo().deviceId,
            payload: {
                query: 'get-provider-config-tab',
                company_id: providerId,
                tab: tab
            }
        };

        const encryptedMessage = encryptDataHybrid(dataToEncrypt);
        if (!encryptedMessage) {
            throw new Error("Encryption failed");
        }

        const response = await fetch(`${API_URL}provider-operation.php`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
            },
            body: JSON.stringify({
                encrypted_key: encryptedMessage.encryptedKey,
                encrypted_data: encryptedMessage.encryptedData,
            }),
        });

        const result = await response.json();
        if (result.status === true) {
            return {
                data: result.data[0] || {},
                table_name: result.table_name || 'provider_setting'
            };
        } else {
            throw new Error(result.message || "Failed to fetch configuration");
        }
    };

    const transformConfigData = (data, category) => {
        const transformed = [];

        // Add Request Section Header
        transformed.push({
            name: `${category.charAt(0).toUpperCase() + category.slice(1)} - Request Parameters`,
            type: 'section',
            category,
            section: 'request'
        });

        // Add Request Fields (excluding live_com and live_com_type)
        Object.entries(data).forEach(([key, value]) => {
            if (key !== 'sl' && key !== 'company_id' && key !== 'category' &&
                !['msg', 'balance', 'operator_ref',
                    'status', 'status_success', 'status_failed', 'status_pending',
                    'live_com', 'live_com_type'].includes(key)) {

                transformed.push({
                    name: key,
                    value: value || '',
                    type: key.toLowerCase().includes('key') || key.toLowerCase().includes('token') ? 'password' : 'text',
                    category,
                    section: 'request',
                    description: getFieldDescription(key, 'request')
                });
            }
        });

        // Add Response Section Header
        transformed.push({
            name: `${category.charAt(0).toUpperCase() + category.slice(1)} - Response Handling`,
            type: 'section',
            category,
            section: 'response'
        });

        // Add Response Fields
        const responseFields = [
            'response_format', 'msg', 'balance',
            'operator_ref', 'status', 'status_success',
            'status_failed', 'status_pending', 'live_com', 'live_com_type'
        ];

        responseFields.forEach(key => {
            // Hide specific fields based on category
            if (category === 'balance' && (key === 'operator_ref' || key === 'live_com' || key === 'live_com_type')) return;
            if (category === 'complaint' && (key === 'balance' || key === 'operator_ref' || key === 'live_com' || key === 'live_com_type')) return;

            if (data.hasOwnProperty(key)) {
                // Special handling for live_com_type field
                if (key === 'live_com_type') {
                    transformed.push({
                        name: key,
                        value: data[key] || '',  // Fixed: Changed from value to data[key]
                        type: 'select',
                        options: [
                            { value: '1', label: 'Percentage' },
                            { value: '2', label: 'Flat' }
                        ],
                        category,
                        section: 'response',
                        description: getFieldDescription(key, 'response')
                    });
                } else {
                    transformed.push({
                        name: key,
                        value: data[key] || '',
                        type: key === 'live_com' ? 'text' :
                            (key.toLowerCase().includes('key') || key.toLowerCase().includes('token') ? 'password' : 'text'),
                        category,
                        section: 'response',
                        description: getFieldDescription(key, 'response')
                    });
                }
            } else {
                // Ensure all response fields exist even if not in initial data
                transformed.push({
                    name: key,
                    value: '',
                    type: key === 'live_com_type' ? 'select' : 'text',
                    options: key === 'live_com_type' ? [
                        { value: '1', label: 'Percentage' },
                        { value: '2', label: 'Flat' }
                    ] : undefined,
                    category,
                    section: 'response',
                    description: getFieldDescription(key, 'response')
                });
            }
        });

        return transformed;
    };

    const getFieldDescription = (key, section) => {
        const descriptions = {
            request: {
                url: "API endpoint URL for this operation",
                method: "HTTP method to use (GET, POST, etc.)",
                request_id: "Field name for transaction ID in API request",
                operator_code: "Field name for operator code in API request",
                amount: "Field name for amount in API request",
                number: "Field name for mobile number in API request",
                key: "Authentication key parameter name",
                key_value: "Authentication key/secret value",
                param1_name: "Additional parameter 1 name",
                param1_value: "Additional parameter 1 value"
            },
            response: {
                response_format: "Expected response format (JSON/XML)",
                msg: "Field name for response message in API output",
                remark: "Field name for additional remarks in response",
                balance: "Field name for balance in API response",
                operator_ref: "Field name for operator reference ID in response",
                status: "Field name for transaction status in response",
                status_success: "Value that indicates successful transaction",
                status_failed: "Value that indicates failed transaction",
                status_pending: "Value that indicates pending transaction",
                live_com: "Commission calculation field in response",
                live_com_type: "Commission type in response (percentage=1, flat=2)"
            }
        };

        return descriptions[section]?.[key] || '';
    };

    const handleConfigChange = (name, value) => {
        setCurrentTabData(prev => {
            const updatedAttributes = prev.attributes.map(attr => {
                if (attr.name === name) {
                    return { ...attr, value };
                }
                return attr;
            });
            return {
                ...prev,
                attributes: updatedAttributes
            };
        });
    };

    const saveSectionConfig = async () => {
        try {
            const sectionAttributes = currentTabData.attributes.filter(attr => attr.type !== 'section');
            const configDataToSave = {};

            sectionAttributes.forEach(attr => {
                configDataToSave[attr.name] = attr.value;
            });

            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    query: 'set-provider-config-tab',
                    company_id: providerId,
                    tab: activeConfigTab,
                    table_name: tableName,
                    config_data: configDataToSave
                }
            };

            console.log(dataToEncrypt);

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);
            if (!encryptedMessage) {
                throw new Error("Encryption failed");
            }

            const response = await fetch(`${API_URL}provider-operation.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify({
                    encrypted_key: encryptedMessage.encryptedKey,
                    encrypted_data: encryptedMessage.encryptedData,
                }),
            });

            const result = await response.json();
            if (result.status === true) {
                toast.success(`${activeConfigTab} configuration saved successfully`);
                handleTabChange(activeConfigTab);
            } else {
                throw new Error(result.message || "Failed to save configuration");
            }
        } catch (error) {
            toast.error(error.message || `Failed to save ${activeConfigTab} configuration`);
        }
    };

    const renderConfigAttribute = (attr) => {
        if (attr.type === 'section') {
            return (
                <div key={attr.name} className="pt-4">
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                        {attr.name}
                    </h4>
                </div>
            );
        }

        return (
            <div key={attr.name} className="grid grid-cols-12 gap-4 items-center">
                <div className="col-span-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        {attr.name.replace(/_/g, ' ')}
                    </label>
                    {attr.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {attr.description}
                        </p>
                    )}
                </div>
                <div className="col-span-8">
                    <div className="relative">
                        {attr.name === 'method' ? (
                            <select
                                value={attr.value || ''}
                                onChange={(e) => handleConfigChange(attr.name, e.target.value)}
                                required
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">Select HTTP Method</option>
                                <option value="GET">GET</option>
                                <option value="POST">POST</option>
                                <option value="TEXT">TEXT</option>
                            </select>
                        ) : attr.name === 'response_format' ? (
                            <select
                                value={attr.value || ''}
                                onChange={(e) => handleConfigChange(attr.name, e.target.value)}
                                required
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">Select Response Format</option>
                                <option value="JSON">JSON</option>
                                <option value="XML">XML</option>
                            </select>
                        ) : attr.name === 'live_com_type' ? (
                            <select
                                value={attr.value || ''}
                                onChange={(e) => handleConfigChange(attr.name, e.target.value)}
                                required
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">Select Commission Type</option>
                                {attr.options.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <>
                                <input
                                    type={attr.type}
                                    value={attr.value || ''}
                                    onChange={(e) => handleConfigChange(attr.name, e.target.value)}
                                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md shadow-sm py-2 px-3 text-sm bg-white dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 pr-10"
                                    placeholder={`Enter ${attr.name.replace(/_/g, ' ')}`}
                                />
                                {attr.type === 'password' && (
                                    <button
                                        type="button"
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                        onClick={() => {
                                            setCurrentTabData(prev => {
                                                const updatedAttributes = prev.attributes.map(a => {
                                                    if (a.name === attr.name) {
                                                        return { ...a, type: a.type === 'password' ? 'text' : 'password' };
                                                    }
                                                    return a;
                                                });
                                                return {
                                                    ...prev,
                                                    attributes: updatedAttributes
                                                };
                                            });
                                        }}
                                    >
                                        {attr.type === 'password' ? (
                                            <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                                        ) : (
                                            <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                                        )}
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-white">
            <div className="flex flex-col flex-1">
                <Header />
                <main className="flex-1 p-6 overflow-auto">
                    <div className="max-w-7xl mx-auto">
                        <div className="flex items-center justify-between mb-6">
                            <button
                                onClick={() => navigate(-1)}
                                className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-600"
                            >
                                <ChevronLeftIcon className="h-5 w-5 mr-1" />
                                Back to Providers
                            </button>
                            <h2 className="text-xl font-semibold">
                                {companyName} - API Configuration
                            </h2>
                            <div className="w-24"></div>
                        </div>

                        {loading ? (
                            <div className="flex justify-center items-center py-8">
                                <motion.div
                                    className="fixed inset-0 z-[100] bg-black/30 backdrop-blur-md flex items-center justify-center"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <div className="flex flex-col items-center justify-center space-y-4">
                                        <div className="relative h-16 w-16">
                                            <div className="absolute inset-0 rounded-full border-4 border-white/10"></div>
                                            <motion.div
                                                className="absolute inset-0 rounded-full border-4 border-transparent border-t-white border-r-white"
                                                animate={{ rotate: 360 }}
                                                transition={{
                                                    repeat: Infinity,
                                                    ease: "linear",
                                                    duration: 1
                                                }}
                                                style={{
                                                    borderTopLeftRadius: "9999px",
                                                    borderTopRightRadius: "9999px",
                                                    borderBottomLeftRadius: "9999px",
                                                    borderBottomRightRadius: "9999px"
                                                }}
                                            />
                                        </div>
                                        <motion.p
                                            className="text-white font-medium text-sm tracking-wide"
                                            initial={{ opacity: 0, y: 5 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.1 }}
                                        >
                                            Loading...
                                        </motion.p>
                                    </div>
                                </motion.div>
                            </div>
                        ) : (
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden">
                                <div className="border-b border-gray-200 dark:border-gray-700">
                                    <nav className="flex -mb-px">
                                        {['Recharge', 'Balance', 'Complaint', 'Callback', 'Status'].map((tab) => (
                                            <button
                                                key={tab}
                                                onClick={() => handleTabChange(tab.toLowerCase())}
                                                className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${activeConfigTab === tab.toLowerCase()
                                                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                                                    }`}
                                            >
                                                {tab}
                                            </button>
                                        ))}
                                    </nav>
                                </div>

                                <div className="p-6">
                                    {currentTabData.loading ? (
                                        <div className="flex justify-center items-center py-8">
                                            <motion.div
                                                className="fixed inset-0 z-[100] bg-black/30 backdrop-blur-md flex items-center justify-center"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0 }}
                                                transition={{ duration: 0.2 }}
                                            >
                                                <div className="flex flex-col items-center justify-center space-y-4">
                                                    <div className="relative h-16 w-16">
                                                        <div className="absolute inset-0 rounded-full border-4 border-white/10"></div>
                                                        <motion.div
                                                            className="absolute inset-0 rounded-full border-4 border-transparent border-t-white border-r-white"
                                                            animate={{ rotate: 360 }}
                                                            transition={{
                                                                repeat: Infinity,
                                                                ease: "linear",
                                                                duration: 1
                                                            }}
                                                            style={{
                                                                borderTopLeftRadius: "9999px",
                                                                borderTopRightRadius: "9999px",
                                                                borderBottomLeftRadius: "9999px",
                                                                borderBottomRightRadius: "9999px"
                                                            }}
                                                        />
                                                    </div>
                                                    <motion.p
                                                        className="text-white font-medium text-sm tracking-wide"
                                                        initial={{ opacity: 0, y: 5 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ delay: 0.1 }}
                                                    >
                                                        Loading...
                                                    </motion.p>
                                                </div>
                                            </motion.div>
                                        </div>
                                    ) : (
                                        <div className="space-y-8">
                                            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                                                <div className="space-y-6">
                                                    {currentTabData.attributes
                                                        .filter(attr => attr.section?.toLowerCase() === 'request')
                                                        .map(renderConfigAttribute)}
                                                </div>
                                            </div>

                                            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                                                <div className="space-y-6">
                                                    {currentTabData.attributes
                                                        .filter(attr => attr.section?.toLowerCase() === 'response')
                                                        .map(renderConfigAttribute)}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="mt-6 flex justify-end">
                                        <button
                                            onClick={saveSectionConfig}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center"
                                        >
                                            Save {activeConfigTab.charAt(0).toUpperCase() + activeConfigTab.slice(1)} API
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </main>
            </div>

            <ToastContainer />
        </div>
    );
};

export default RechargeAPIConfiguration;